import React, { createContext, forwardRef, useContext, useMemo } from 'react';
import { useFormatCurrency } from '@udemy/ud-data';
import { SubscriptionPlanSubscribedState, SubscriptionPlanIsLoading } from '../graphql/types/subscription-plan';
import { adapterFor } from '../graphql/types/subscription-plan-adapters';
import { GraphQLSubscriptionPlanProvider, GraphQLSubscriptionPlansContext } from './graphql-subscription-plan-provider.react-component';
const defaultSubscriptionPlansContext = {
  CONSUMERSUBSCRIPTION: undefined,
  CONSUMERSUBSCRIPTION_VALUE_PLAN: undefined,
  GROW_WITH_GOOGLE: undefined,
  CONSUMERSUBSCRIPTION_PLUS_GROW_WITH_GOOGLE: undefined,
  ENTERPRISE: undefined,
  ENTERPRISE_AI_GROWTH: undefined,
  ENTERPRISE_AI_READINESS: undefined,
  ENTERPRISE_BASE: undefined,
  ENTERPRISEPRO: undefined,
  TEAM: undefined,
  UDEMYPRO: undefined
};
export const ReactSubscriptionsContext = /*#__PURE__*/createContext(defaultSubscriptionPlansContext);

/**
 * Defines the props passed to the SubscriptionPlansProvider component.
 * Specifically, this includes an array of SubscriptionPlanProductTypes to load and
 * include in the context object shared via provider to child components.
 */

/**
 * SubscriptionPlansProvider
 *
 * Public provider that wraps the GraphQLSubscriptionPlanProvider and adds currency formatting.
 * This provider automatically re-adapts subscription plans when the currency formatter changes,
 * without re-fetching data from the GraphQL API.
 *
 * Architecture:
 * 1. GraphQLSubscriptionPlanProvider (inner) - fetches and caches raw GraphQL data
 * 2. SubscriptionPlansProviderInternal (middle) - adapts raw data with currency formatting
 * 3. SubscriptionPlansProvider (outer) - public API wrapper
 */
export const SubscriptionPlansProvider = /*#__PURE__*/forwardRef((props, ref) => {
  return /*#__PURE__*/React.createElement(GraphQLSubscriptionPlanProvider, {
    ref: ref,
    subscriptionPlanQueryVariables: props.subscriptionPlanQueryVariables
  }, /*#__PURE__*/React.createElement(SubscriptionPlansProviderInternal, null, props.children));
});

/**
 * Internal provider that consumes raw GraphQL data and adapts it with currency formatting.
 * This component re-runs adaptation whenever the currency formatter changes, ensuring
 * prices are always displayed in the correct currency without refetching data.
 *
 * Note: This component does NOT need forwardRef because the refetch functionality
 * is handled by the GraphQLSubscriptionPlanProvider above it in the tree.
 */
const SubscriptionPlansProviderInternal = props => {
  // Get the current currency formatter - when this changes, we re-adapt
  const currencyFormatter = useFormatCurrency();

  // Get GraphQL subscription plan data from the GraphQL provider
  const graphqlPlansContext = useContext(GraphQLSubscriptionPlansContext);

  // Adapt GraphQL plans with current currency formatter
  // This memoization ensures we only re-adapt when graphqlPlansContext or currencyFormatter changes
  const adaptedPlansContext = useMemo(() => {
    const adapted = {};
    Object.entries(graphqlPlansContext).forEach(([productTypeKey, graphqlData]) => {
      const productType = productTypeKey;
      if (!graphqlData) {
        adapted[productType] = undefined;
        return;
      }
      if (graphqlData.isLoading) {
        adapted[productType] = SubscriptionPlanIsLoading;
        return;
      }

      // If user is subscribed to this specific plan, return the subscribed state
      if (graphqlData.isSubscriberToSpecificPlan) {
        adapted[productType] = {
          ...SubscriptionPlanSubscribedState,
          productType: productType
        };
        return;
      }

      // If we have a plan, adapt it with the current currency formatter
      if (graphqlData.subscriptionPlan && Object.keys(graphqlData.subscriptionPlan).length > 0) {
        const adapter = adapterFor(productType);
        const adaptedPlan = adapter.adaptSubscriptionPlan({
          ...graphqlData.subscriptionPlan,
          isSubscriber: graphqlData.isSubscriber,
          isSubscriberToSpecificPlan: graphqlData.isSubscriberToSpecificPlan,
          isLoading: false
        }, currencyFormatter);
        adapted[productType] = adaptedPlan;
      } else if (graphqlData.isSubscriber) {
        // User is subscribed to some plan but not this specific one
        adapted[productType] = {
          isSubscriber: true,
          isSubscriberToSpecificPlan: false,
          isLoading: false
        };
      } else {
        // Error case or no data: empty plan data with isLoading: false indicates a fetch error
        // We return undefined for error cases to maintain compatibility
        adapted[productType] = undefined;
      }
    });
    return adapted;
  }, [graphqlPlansContext, currencyFormatter]);
  return /*#__PURE__*/React.createElement(ReactSubscriptionsContext.Provider, {
    value: adaptedPlansContext
  }, props.children);
};
//# sourceMappingURL=subscription-plan-provider.react-component.js.map